<template>
  <div class="configurator">
    <div class="configurator__main">
      <div class="configurator__settings">
        <!-- Presets Tabs -->
        <div class="configurator__presets">
          <button
            type="button"
            class="configurator__preset-tab"
            :class="{ 'configurator__preset-tab--active': currentPreset === 'online_booking' }"
            @click="applyPreset('online_booking')"
          >
            <BaseIcon2
              :class="{ 'configurator__preset-tab--active': currentPreset === 'online_booking' }"
              id="icon-online-calendar"
              color="var(--grey-3)"
              height="16"
              width="16"
            />
            <span class="typography__title3">{{ t("entities.rate.calculator.presetOnlineBooking") }}</span>
          </button>
          <button
            type="button"
            class="configurator__preset-tab"
            :class="{ 'configurator__preset-tab--active': currentPreset === 'online_store' }"
            @click="applyPreset('online_store')"
          >
            <BaseIcon2
              :class="{ 'configurator__preset-tab--active': currentPreset === 'online_store' }"
              id="icon-online-shop"
              color="var(--grey-3)"
              height="16"
              width="16"
            />
            <span class="typography__title3">{{ t("entities.rate.calculator.presetOnlineStore") }}</span>
          </button>
        </div>

        <!-- Branches Slider -->
        <div class="configurator__setting">
          <div class="configurator__label-row">
            <label class="configurator__label-text typography__title2">{{
              t("entities.rate.calculator.branches")
            }}</label>
            <span class="configurator__value-display typography__headline3">{{ branches }}</span>
          </div>
          <input
            v-model.number="branches"
            type="range"
            min="1"
            :max="pricingConfig.max_branches"
            step="1"
            class="configurator__slider"
          />
        </div>

        <!-- Employees Slider -->
        <div class="configurator__setting">
          <div class="configurator__label-row">
            <label class="configurator__label-text typography__title2">{{
              t("entities.rate.calculator.employees")
            }}</label>
            <span class="configurator__value-display typography__headline3">{{ employees }}</span>
          </div>
          <input
            v-model.number="employees"
            type="range"
            min="0"
            :max="pricingConfig.max_online_employees"
            step="1"
            class="configurator__slider"
          />
        </div>

        <!-- Products Slider -->
        <div class="configurator__setting">
          <div class="configurator__label-row">
            <label class="configurator__label-text typography__title2">{{
              t("entities.rate.calculator.products")
            }}</label>
            <span class="configurator__value-display typography__headline3">{{ currentProductTier.limit }}</span>
          </div>
          <input
            v-model.number="productTierIndex"
            type="range"
            min="0"
            :max="pricingConfig.product_prices.length - 1"
            step="1"
            class="configurator__slider"
          />
        </div>

        <BaseDivider class="ui-divider-primary" />

        <!-- Payment Period -->
        <div class="configurator__setting">
          <label class="typography__title2">{{ t("entities.rate.calculator.period") }}</label>
          <div class="configurator__periods">
            <button
              v-for="period in periods"
              :key="period.key"
              type="button"
              class="configurator__period"
              :class="{ 'configurator__period--active': selectedPeriod === period.key }"
              @click="selectedPeriod = period.key"
            >
              <span class="configurator__period-name">{{ period.label }}</span>
              <span
                v-if="period.discount > 0"
                class="configurator__period-discount"
              >
                -{{ period.discount }}%
              </span>
            </button>
          </div>
        </div>

        <BaseDivider class="ui-divider-primary" />

        <!-- First Payment Promo -->
        <div class="configurator__promo-toggle">
          <div class="configurator__promo-info">
            <div class="typography__title2">{{ t("entities.rate.promocode.title") }}</div>
            <div
              class="typography__body2 configurator__promo-text"
              v-html="t('entities.rate.promocode.desc')"
            />
          </div>
          <BaseToggle2
            v-model="isPromoActive"
            type="checkbox"
            class="base-switch-first"
            name="isPromoActive"
          />
        </div>
      </div>

      <!-- Result Card -->
      <div class="configurator__result">
        <div class="configurator__support-tier-info">
          <div class="configurator__support-tier-label typography__body2">
            {{ t("entities.rate.calculator.supportLabel") }}
          </div>
          <div class="configurator__support-tier-value typography__title1">
            {{ t(`entities.rate.calculator.supportType.${supportType}`) }}
          </div>
        </div>

        <div class="configurator__price-block">
          <div class="configurator__price-label typography__body1">
            {{ t("entities.rate.calculator.monthlyTotal") }}
          </div>
          <div class="configurator__price-values">
            <div
              v-if="finalMonthly < monthlyBase"
              class="configurator__price-old typography__body2"
            >
              ${{ monthlyBase.toFixed(2) }}
            </div>
            <div class="configurator__price-value typography__headline2">${{ finalMonthly.toFixed(2) }}</div>
          </div>
        </div>

        <div class="configurator__price-block configurator__price-block--total">
          <div class="configurator__price-label typography__title1">{{ t("entities.rate.calculator.totalToPay") }}</div>
          <div class="configurator__price-values">
            <div
              v-if="totalToPay < originalTotal"
              class="configurator__price-old typography__title2"
            >
              ${{ originalTotal.toFixed(2) }}
            </div>
            <div class="configurator__price-value typography__headline1">${{ totalToPay.toFixed(2) }}</div>
          </div>
        </div>

        <template v-if="lead === 'project'">
          <BaseLink
            class="base-button-extra-big configurator__submit-btn main-base-button-first typography__title2"
            href="/client/projects?force=true"
          >
            {{ t("rates.rateItemTryFreeButton") }}
          </BaseLink>
        </template>
        <BaseModalButton
          v-else
          class="base-button-extra-big configurator__submit-btn main-base-button-first typography__title2"
          :modal="MODAL.AuthRegistration"
        >
          {{ t("rates.rateItemTryFreeButton") }}
        </BaseModalButton>
        <div class="lead-hint">{{ t("common.lead.hint") }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  rate: any;
  lead?: "reg";
}

const props = defineProps<Props>();
const { t } = useI18n();

const pricingConfig = computed(() => props.rate.pricing_config);

const branches = ref(1);
const employees = ref(1);
const productTierIndex = ref(1);
const selectedPeriod = ref("monthly");
const isPromoActive = ref(true);

const periods = [
  { key: "monthly", label: t("entities.project.rateCheckoutTypePaymentValueOneMonths"), months: 1, discount: 0 },
  { key: "three_months", label: t("entities.project.rateCheckoutTypePaymentValueThreeMonths"), months: 3, discount: 5 },
  { key: "six_months", label: t("entities.project.rateCheckoutTypePaymentValueSixMonths"), months: 6, discount: 10 },
  { key: "yearly", label: t("entities.project.rateCheckoutTypePaymentValueTwelvMonths"), months: 12, discount: 20 },
];

const currentProductTier = computed(() => pricingConfig.value.product_prices[productTierIndex.value]);

const monthlyBase = computed(() => {
  return (
    pricingConfig.value.base_price +
    branches.value * pricingConfig.value.branch_price +
    employees.value * pricingConfig.value.employee_online_price +
    currentProductTier.value.price
  );
});

const periodDiscount = computed(() => {
  const p = periods.find((p) => p.key === selectedPeriod.value);
  return (p?.discount || 0) / 100;
});

const personalDiscount = computed(() => (props.rate.personal_discount || 0) / 100);

const promoDiscount = computed(() => (isPromoActive.value ? 0.15 : 0));

const finalMonthly = computed(() => {
  return monthlyBase.value * (1 - periodDiscount.value) * (1 - personalDiscount.value) * (1 - promoDiscount.value);
});

const totalToPay = computed(() => {
  const p = periods.find((p) => p.key === selectedPeriod.value);
  return finalMonthly.value * (p?.months || 1);
});

const originalTotal = computed(() => {
  const p = periods.find((p) => p.key === selectedPeriod.value);
  return monthlyBase.value * (p?.months || 1);
});

const isMinConfig = computed(() => {
  return branches.value === 1 && employees.value <= 1 && currentProductTier.value.limit <= 30;
});

const isMaxConfigReached = computed(() => {
  return branches.value >= 10 || employees.value >= 20 || currentProductTier.value.limit >= 1000;
});

const supportType = computed(() => {
  if (isMaxConfigReached.value) return "personal";
  if (isMinConfig.value) return "base";
  return "priority";
});

const currentPreset = computed(() => {
  const isOnlineBooking = branches.value === 1 && employees.value === 3 && currentProductTier.value.limit === 0;
  if (isOnlineBooking) return "online_booking";

  const isOnlineStore = branches.value === 1 && employees.value === 0 && currentProductTier.value.limit === 60;
  if (isOnlineStore) return "online_store";

  return null;
});

const applyPreset = (preset: "online_booking" | "online_store") => {
  if (preset === "online_booking") {
    branches.value = 1;
    employees.value = 3;
    const tierIdx = pricingConfig.value.product_prices.findIndex((p: any) => p.limit === 0);
    if (tierIdx !== -1) productTierIndex.value = tierIdx;
  } else if (preset === "online_store") {
    branches.value = 1;
    employees.value = 0;
    const tierIdx = pricingConfig.value.product_prices.findIndex((p: any) => p.limit === 60);
    if (tierIdx !== -1) productTierIndex.value = tierIdx;
  }
};
</script>

<style scoped>
.configurator {
  width: 100%;
}

.configurator__main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (--desktop) {
  .configurator__main {
    flex-direction: row;
    align-items: flex-start;
  }
}

.configurator__settings {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: var(--white);
  border-radius: 32px;
  border: 1px solid var(--grey-4);
}

@media (--desktop) {
  .configurator__settings {
    gap: 32px;
    padding: 40px;
  }
}

.configurator__setting {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.configurator__label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.configurator__label-text {
  color: var(--grey-1);
}

.configurator__value-display {
  color: var(--orange-1);
}

.configurator__slider {
  width: 100%;
  height: 8px;
  background: var(--grey-7);
  border-radius: 999px;
  -webkit-appearance: none;
  cursor: pointer;
  outline: none;
}

.configurator__slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 24px;
  height: 24px;
  background: var(--orange-1);
  border-radius: 50%;
  border: 4px solid var(--white);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.configurator__presets {
  display: flex;
  border-bottom: 1px solid var(--grey-5);
  margin-bottom: 8px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.configurator__presets::-webkit-scrollbar {
  display: none;
}

.configurator__preset-tab {
  flex: 1;
  padding: 0 0 8px 0;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  cursor: pointer;
  color: var(--grey-2);
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
  flex-direction: column;
}

@media (--desktop) {
  .configurator__preset-tab {
    padding: 16px;
    flex-direction: row;
  }
}

.configurator__preset-tab:hover {
  color: var(--grey-1);
  background: var(--grey-7);
}

.configurator__preset-tab--active {
  color: var(--orange-1);
  border-bottom-color: var(--orange-1);
}

.configurator__periods {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (--desktop) {
  .configurator__periods {
    display: flex;
    gap: 16px;
  }
}

.configurator__period {
  flex: 1;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: var(--grey-7);
  border-radius: 16px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.configurator__period--active {
  background: var(--white);
  border-color: var(--orange-1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.configurator__period-discount {
  padding: 2px 6px;
  background: var(--green);
  color: white;
  border-radius: 8px;
  font-size: 11px;
}

.configurator__result {
  width: 100%;
  padding: 24px;
  background: var(--grey-7);
  border-radius: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

@media (--desktop) {
  .configurator__result {
    position: sticky;
    top: 24px;
    max-width: 360px;
    padding: 32px;
    gap: 24px;
  }
}

.configurator__price-block--total {
  padding-top: 16px;
  border-top: 1px solid var(--grey-5);
}

.configurator__price-values {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 8px;
}

.configurator__price-old {
  text-decoration: line-through;
  color: var(--grey-3);
}

.configurator__submit-btn {
  width: 100%;
  border-radius: 30px;
}

.configurator__support-tier-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.configurator__promo-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid var(--orange-1);
}

.configurator__promo-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.configurator__promo-text {
  color: var(--grey-2);
}

.configurator__promo-code {
  color: var(--orange-1);
  font-weight: 700;
}

.lead-hint {
  color: var(--grey-2);
  margin-top: -15px;
  text-align: center;
}
</style>
